import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    FormDateField,
    FormNumberField,
    FormSelect,
    FormTextarea,
    FormTextField,
    SuggestNameButton,
} from '@/components/organisms/FishGroupForms';
import type { DomainFishType } from '@/domain/cages';
import { plannedSeasonCode } from '@/domain/stockingCycles';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import type { BoardCycle, EmptyPen } from '../../types';

import type { NewFishGroupFormValues } from './newFishGroupForm';
import { earliestStartDate, isBeforeEarliestStart } from './startDateLimit';

import styles from './NewFishGroupModal.module.scss';

interface NewFishGroupFieldsProps {
    control: Control<NewFishGroupFormValues>;
    setValue: UseFormSetValue<NewFishGroupFormValues>;
    cycles: BoardCycle[];
    pens: EmptyPen[];
    /** The pen list hasn't loaded yet, so an empty `pens` says nothing about the site. */
    pensLoading: boolean;
    /** Which pens are free couldn't be checked; the modal says so and offers a retry. */
    pensFailed: boolean;
    /** Real names of the site's groups, which Suggest won't repeat. */
    existingNames: readonly string[];
    fishTypes: DomainFishType[];
}

/** The internal create form's fields, plus Cycle, Pen and Species for customers. */
export default function NewFishGroupFields(props: NewFishGroupFieldsProps) {
    const { control, setValue, cycles, pens, pensLoading, pensFailed, existingNames, fishTypes } =
        props;
    const pensKnown = !pensLoading && !pensFailed;
    const { t, i18n } = useTranslation('fishGroups');
    const cageId = useWatch({ control, name: 'cageId' });
    const cycleId = useWatch({ control, name: 'stockingCycleId' });
    // The real pen name: Suggest's output is saved, so it must never come from a demo label.
    const penName = pens.find((pen) => String(pen.cageId) === cageId)?.cageName ?? null;
    const cycle = cycles.find((candidate) => String(candidate.id) === cycleId);
    const season = plannedSeasonCode(cycle?.plannedStartDate ?? null);
    const earliest = earliestStartDate();

    return (
        <>
            <div className={styles.grid}>
                <FormSelect
                    control={control}
                    name="stockingCycleId"
                    label={t('newFishGroup.fields.cycle')}
                    placeholder={t('newFishGroup.fields.cyclePlaceholder')}
                    options={cycles.map((cycle) => ({
                        value: String(cycle.id),
                        label: t('board.cycleSelector.option', {
                            name: cycle.displayName,
                            status: t(`board.cycleStatus.${cycle.status}`),
                        }),
                    }))}
                    rules={{ required: true }}
                />
                <FormSelect
                    control={control}
                    name="cageId"
                    label={t('newFishGroup.fields.pen')}
                    placeholder={t(
                        pensLoading
                            ? 'newFishGroup.fields.penLoading'
                            : 'newFishGroup.fields.penPlaceholder'
                    )}
                    options={pens.map((pen) => ({
                        value: String(pen.cageId),
                        label: t('newFishGroup.fields.penOption', { pen: pen.displayName }),
                    }))}
                    note={pensKnown && pens.length === 0 ? t('newFishGroup.noEmptyPens') : null}
                    disabled={!pensKnown}
                    rules={{ required: true }}
                />
                <FormDateField
                    control={control}
                    name="startDate"
                    label={t('actions.fields.startDate')}
                    placeholder={t('actions.fields.startDatePlaceholder')}
                    blockDaysBefore={earliest}
                    rules={{
                        required: true,
                        validate: (value) =>
                            !(value instanceof Date) ||
                            !isBeforeEarliestStart(value, earliest) ||
                            t('newFishGroup.errors.startDateTooEarly', {
                                date: formatDateShortLocalized(
                                    earliest,
                                    parseLocale(i18n.language)
                                ),
                            }),
                    }}
                />
                <FormSelect
                    control={control}
                    name="fishTypeId"
                    label={t('newFishGroup.fields.species')}
                    options={fishTypes.map((type) => ({
                        value: String(type.id),
                        label: type.name,
                    }))}
                    rules={{ required: true }}
                />
            </div>
            <FormTextField
                control={control}
                name="name"
                label={t('actions.fields.name')}
                rules={{
                    validate: (value) =>
                        (typeof value === 'string' && value.trim() !== '') ||
                        t('actions.errors.required'),
                }}
                labelAction={
                    <SuggestNameButton
                        cageName={penName}
                        operation="create"
                        season={season}
                        existingNames={existingNames}
                        onApply={(value) =>
                            setValue('name', value, { shouldDirty: true, shouldValidate: true })
                        }
                    />
                }
            />
            <FormNumberField
                control={control}
                name="initialPopulationSize"
                label={t('actions.fields.initialPopulationSize')}
                placeholder={t('newFishGroup.fields.populationPlaceholder')}
                optional
                min={1}
                rules={{
                    validate: (value) =>
                        value === '' ||
                        (Number.isInteger(Number(value)) && Number(value) >= 1) ||
                        t('newFishGroup.errors.population'),
                }}
            />
            <FormTextField
                control={control}
                name="reason"
                label={t('actions.fields.reason')}
                optional
            />
            <FormTextarea
                control={control}
                name="notes"
                label={t('actions.fields.notes')}
                optional
            />
        </>
    );
}
